
/**
 * Application URL paths (frontend router paths — NOT API routes).
 *
 * `as const` makes every value a string literal so {@link RoutePath} can be
 * derived as a union. Every value here MUST have a matching <Route path> in
 * `App.tsx`, and every <Route path> MUST derive from a key here. Parameterized
 * routes keep their `:param` pattern (used for registration); build concrete
 * URLs for navigation with {@link buildRoute}.
 */
export const ROUTES = {
    main: "/",
    home: "/home",

    // Auth
    login: "/login",
    register: "/register",
    verifyEmail: "/verify-email",
    forgotPassword: "/forgot-password",
    changePassword: "/change-password",

    // Main
    profile: "/profile",

    // Projects
    projects: "/proyectos",
    newProject: "/proyectos/nuevo",

    // Editor
    editor: "/editor/:projectId",

    // Orders
    orders: "/pedidos",
    orderDetail: "/pedidos/:uuid",
    orderCheckout: "/editor/:projectId/imprimir",
    cart: "/carrito",
    checkout: "/checkout",

    // Admin
    admin: "/admin",
    adminDashboard: "/admin/dashboard",
    adminMaterials: "/admin/materiales",
    adminSizes: "/admin/tamanos",
    adminPrices: "/admin/precios",
    adminOrders: "/admin/pedidos",
    adminShipping: "/admin/envios",

    // Canva
    canvaCallback: "/canva/callback",

    // Legal
    privacyPolicy: "/privacy",
    termsOfService: "/terms",

    notFound: "*",
} as const;

/**
 * Union of every registered route pattern. Any `<Link to>` / `navigate()` /
 * `<Navigate to>` target should resolve to one of these (use {@link buildRoute}
 * for the parameterized ones) so a dead link becomes a compile-time error.
 */
export type RoutePath = (typeof ROUTES)[keyof typeof ROUTES];

/**
 * Build concrete URLs for parameterized routes from the patterns in
 * {@link ROUTES}. Never hand-write `/editor/${id}` or `/pedidos/${uuid}` —
 * route them through here so a path rename only needs to change ROUTES.
 */
export const buildRoute = {
    editor: (projectId: string | number) =>
        ROUTES.editor.replace(":projectId", String(projectId)),
    orderCheckout: (projectId: string | number) =>
        ROUTES.orderCheckout.replace(":projectId", String(projectId)),
    orderDetail: (uuid: string) => ROUTES.orderDetail.replace(":uuid", uuid),
} as const;

export const API_ROUTES = {
    // Auth
    login: "/auth/login/",
    logout: "/auth/logout/",
    refresh: "/auth/token/refresh/",
    signup: "/auth/registration/",
    resendEmail: "/resend-email-confirmation/",
    confirmEmail: "/auth/registration/account-confirm-email/",
    changePassword: "/auth/password/change/",
    resetPassword: "/auth/password/reset/",
    resetPasswordConfirm: "/auth/password/reset/confirm/",

    // Google Auth
    googleLogin: "/auth/google/",

    // Other
    profile: "/api/auth/profile/",

    // Addresses
    addresses: "/api/addresses/",

    // Billing
    billingInfo: "/api/billing-info/",

    // Projects
    documentTypes: "/api/document-types/",
    projects: "/api/projects/",
    projectPages: (projectId: string | number) => `/api/projects/${projectId}/pages/`,
    projectPage: (projectId: string | number, pageId: string | number) =>
        `/api/projects/${projectId}/pages/${pageId}/`,

    // Assets
    assets: "/api/assets/",

    // Orders
    materials: "/api/materials/",
    printSizes: "/api/print-sizes/",
    prices: "/api/prices/",
    shippingConfig: "/api/shipping-config/",
    orders: "/api/orders/",

    // Admin
    adminMaterials: "/api/admin/materials/",
    adminSizes: "/api/admin/print-sizes/",
    adminPrices: "/api/admin/prices/",
    adminShipping: "/api/admin/shipping/",
    adminOrders: "/api/admin/orders/",
    adminVolumeTiers: "/api/admin/volume-tiers/",

    // AI Design
    aiDesign: "/api/ai-design/",

    // Canva integration (OAuth2 PKCE proxy)
    canvaToken: "/api/canva/token/",
    canvaRefresh: "/api/canva/refresh/",
    canvaDesigns: "/api/canva/designs/",
    canvaExport: "/api/canva/export/",
    canvaImport: "/api/canva/import/",

    // External document import
    importDocument: "/api/import/document/",
    importGoogleDrive: "/api/import/google-drive/",

    // Checkout (order-on-payment — the order is created ONLY when the payment
    // confirms; the cart is the durable pre-payment state).
    checkoutPay: "/api/checkout/pay/",
    checkoutSessionStatus: (uuid: string) => `/api/checkout/session/${uuid}/status/`,

    // Payments (Mercado Pago — active gateway)
    mercadopagoProcess: "/api/payments/mercadopago/process/",

    // Payments (Culqi — dormant, kept for fallback)
    culqiCharge: "/api/payments/culqi/charge/",
    culqiOrder: "/api/payments/culqi/order/",

    // Payments (Izipay — dormant, kept for fallback)
    izipayCreateToken: "/api/payments/izipay/create-token/",
    izipayVerify: "/api/payments/izipay/verify/",
}

// Only these paths dont require to send a token
// See /lib/axios.ts
export const NO_AUTH_REQUIRED_API_ROUTES = [
    API_ROUTES.login,
    API_ROUTES.refresh,
    API_ROUTES.signup,
    API_ROUTES.resendEmail,
    API_ROUTES.confirmEmail,
    API_ROUTES.resetPassword,
    API_ROUTES.resetPasswordConfirm,
    API_ROUTES.googleLogin,
]